import { useEffect } from 'react';
import { boton } from '../botones';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Instagram } from 'lucide-react';
import type { ModoDePublicacion } from './postsApi';

/**
 * Qué pasa cuando llega la hora de un post: avisarte, o publicarlo solo.
 *
 * Vive en el encabezado del lanzamiento, al lado de Borrar, y no adentro del
 * calendario: es una propiedad del LANZAMIENTO —se guarda en su fila— igual que
 * Publicar o Borrar, no una preferencia de la vista del mes.
 */
export function ToggleDeModo({
  modo,
  onCambiar,
}: {
  modo: ModoDePublicacion;
  onCambiar: (modo: ModoDePublicacion) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Qué hacer cuando llega la hora"
      className="flex shrink-0 items-center rounded-xl border border-neutral-200 p-1"
    >
      <Opcion activa={modo === 'avisar'} onClick={() => onCambiar('avisar')}>
        Avisarme
      </Opcion>
      <Opcion activa={modo === 'automatico'} onClick={() => onCambiar('automatico')}>
        Publicar automáticamente
      </Opcion>
    </div>
  );
}

/** `rounded-lg` (8) adentro de `rounded-xl` (12) con `p-1` (4): 12 = 8 + 4. */
function Opcion({
  activa,
  onClick,
  children,
}: {
  activa: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={`whitespace-nowrap rounded-lg py-1.5 px-3 text-sm transition-colors ${
        activa ? 'bg-neutral-100 font-medium text-brand-black' : 'text-neutral-500 hover:text-brand-black'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Lo que aparece cuando se pide "Publicar automáticamente" sin cuenta conectada.
 *
 * Reemplaza al cartelito de "Sin cuenta conectada" que estaba siempre a la vista:
 * ese ocupaba lugar permanente para avisar de algo que sólo importa en el momento
 * en que se intenta usar. Acá el aviso llega cuando la falta estorba de verdad, y
 * puede llevarte a arreglarlo.
 *
 * El modo NO cambia detrás del modal: quedaría el lanzamiento diciendo que publica
 * solo cuando no tiene con qué.
 */
export function ModalConectarInstagram({
  abierto,
  onCerrar,
}: {
  abierto: boolean;
  onCerrar: () => void;
}) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!abierto) return;
    const alaTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alaTecla);
    return () => document.removeEventListener('keydown', alaTecla);
  }, [abierto, onCerrar]);

  if (!abierto) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[150] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
      onClick={onCerrar}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label="Conectá Instagram"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl bg-white p-6 sm:rounded-3xl"
      >
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-neutral-100 text-neutral-500">
            <Instagram className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-brand-black">Conectá Instagram</h2>
            <p className="mt-1 text-sm text-neutral-500">
              Para que Atlas publique solo necesita la cuenta conectada. Mientras tanto el
              lanzamiento queda en “Avisarme”.
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCerrar}
            className={boton('neutro')}
          >
            Ahora no
          </button>
          <button
            type="button"
            onClick={() => navigate('/configuracion?seccion=instagram')}
            className={boton('primario')}
          >
            Conectar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
